import { FC } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import { Box, Chip, IconButton, Typography, useTheme } from '@mui/material';
import { ChipField } from 'src/components/chip_field';
import { SpecimenType } from 'src/types';
import { SpecimenTypeUtils } from 'src/utils';

export interface LdtOrderFormHeaderProps {
  /** Title displayed in the header (e.g. "Create Order", "Edit Order", "Revise Report"). */
  title: string;
  /** Specimen type used to derive the Order Type chip label. */
  specimenType: SpecimenType;
  /** Called when the close button is clicked. */
  onClose: () => void;
  /**
   * Order number shown in the Order Number chip. When provided the chip is
   * always rendered; when omitted the chip is hidden (new-order case).
   */
  crossOrgId?: string | null;
  /**
   * Tempus ID (OHID) shown in a chip. Only pass this for Tempus workflow forms;
   * the chip is hidden when omitted or when the value is falsy.
   */
  externalId?: string | null;
}

/**
 * Shared header row used across all LDT order create / edit / revise forms.
 *
 * Renders:
 *   [Title]  [Order Number chip?]  [Tempus ID (OHID) chip?]  [Order Type chip]  [×]
 *
 * The Order Number and Tempus ID (OHID) chips are shown only when their
 * respective values are truthy.
 */
export const LdtOrderFormHeader: FC<LdtOrderFormHeaderProps> = ({
  title,
  specimenType,
  onClose,
  crossOrgId,
  externalId,
}) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          flexWrap: 'wrap',
        }}
      >
        <Typography
          variant='h6'
          sx={{
            fontWeight: theme.typography.fontWeightBold,
          }}
        >
          {title}
        </Typography>

        {crossOrgId && (
          <Chip
            label={
              <ChipField
                label='Order Number'
                value={crossOrgId}
                data-testid='order-number'
              />
            }
            color='primary'
            size='small'
            variant='filled'
          />
        )}

        {externalId?.trim() && (
          <Chip
            label={<ChipField label='Tempus ID (OHID)' value={externalId} />}
            color='primary'
            size='small'
            variant='filled'
          />
        )}

        <Chip
          label={
            <ChipField
              label='Order Type'
              value={SpecimenTypeUtils.getSpecimenTypeName(specimenType)}
            />
          }
          color='primary'
          size='small'
          variant='filled'
        />
      </Box>
      <IconButton onClick={onClose} aria-label='Close' size='small'>
        <CloseIcon />
      </IconButton>
    </Box>
  );
};
